/**
 * App.js - Main Application Component
 *
 * This file implements React Router v6+ best practices with:
 * - Lazy loading (React.lazy) for optimal code splitting
 * - Suspense for loading states
 * - Centralized route configuration
 * - SEO-friendly URLs
 * - Protected routes with authentication
 *
 * Performance Benefits:
 * - Reduced initial bundle size
 * - Faster initial page load
 * - On-demand loading of route components
 * - Better user experience with loading states
 */

import React, { Suspense } from "react";

// Bootstrap & Bootswatch Zephyr theme
import "bootswatch/dist/zephyr/bootstrap.min.css";
import "bootstrap/dist/js/bootstrap.bundle.min.js";

// Font Awesome 6
import "@fortawesome/fontawesome-free/css/all.min.css";
import {
  BrowserRouter,
  Route,
  Routes,
  Navigate,
  Outlet,
  useLocation,
} from "react-router-dom";
import { HelmetProvider } from "react-helmet-async";
import { ToastContainer } from "react-toastify";

// Import non-lazy components (always needed)
import Footer from "./components/footer/Footer";
import Navigation from "./components/navigation/Navigation";
import ScrollToTop from "./utils/ScrollToTop";
import ScrollRestoration from "./utils/ScrollHandler";
import { AuthProvider, useAuth } from "./context/AuthContext";
import Loading from "./utils/Loading";
import CookieConsent from "./cookies/CookieConsent";
import FloatingInsuranceHelp from "./components/floatingHelp/FloatingInsuranceHelp";
import AnnouncementPopup from "./components/announcement/AnnouncementPopup";
import usePageTracker from "./utils/usePageTracker";
import useSocketTracker from "./utils/useSocketTracker";

// Import route configuration with lazy-loaded components
import {
  publicRoutes,
  authRoutes,
  protectedRoutes,
  notFoundRoute,
} from "./routes/routeConfig";
import DefaultRouteSEO from "./routes/DefaultRouteSEO";

const PageTracker = () => {
  usePageTracker();
  return null;
};

const Heartbeat = () => {
  useSocketTracker();
  return null;
};

const ConditionalNavigation = () => {
  const { user, loading } = useAuth();

  // Don't render navigation during loading
  if (loading) {
    return null;
  }

  // Hide navigation if user is logged in
  if (user) {
    return null;
  }

  return <Navigation />;
};

// Render the announcement popup on public pages only (not dashboard / auth routes).
const ConditionalAnnouncement = () => {
  const location = useLocation();
  const skip =
    location.pathname.startsWith("/dashboard") ||
    location.pathname.startsWith("/api/auth");
  if (skip) return null;
  return <AnnouncementPopup />;
};

/**
 * Protected Route Component
 * Handles authentication and authorization for protected routes
 */
const ProtectedRoute = ({ children, adminOnly = false }) => {
  const { user, isAdmin, isSuperAdmin, loading } = useAuth();

  // Show loading state while checking authentication
  if (loading) {
    return (
      <div
        style={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          minHeight: "100vh",
        }}
      >
        <Loading />
      </div>
    );
  }
  // Redirect to login if not authenticated
  if (!user) {
    return <Navigate to="/api/auth/login" replace />;
  }
  // Redirect to home if trying to access admin-only route without permissions
  if (adminOnly && !isAdmin && !isSuperAdmin) {
    return <Navigate to="/" replace />;
  }

  return children ? children : <Outlet />;
};

/**
 * Route Renderer Component
 * Renders routes from configuration with lazy loading support
 */
const RouteRenderer = ({ routes, protected: isProtected, adminOnly }) => {
  return routes.map((route, index) => (
    <Route
      key={route.path || index}
      path={route.path}
      element={
        isProtected || route.protected ? (
          <ProtectedRoute adminOnly={adminOnly || route.adminOnly}>
            <DefaultRouteSEO route={route} />
            <route.component />
          </ProtectedRoute>
        ) : (
          <>
            <DefaultRouteSEO route={route} />
            <route.component />
          </>
        )
      }
    />
  ));
};

/**
 * Loading Fallback Component
 * Displayed while lazy-loaded components are being fetched
 */
const LoadingFallback = () => (
  <div
    style={{
      display: "flex",
      justifyContent: "center",
      alignItems: "center",
      minHeight: "50vh",
    }}
  >
    <Loading />
  </div>
);

/**
 * Main App Component
 */
function App() {
  return (
    <HelmetProvider>
      <div className="home-app">
        <BrowserRouter>
          <AuthProvider>
            <ScrollRestoration />
            <PageTracker />
            <Heartbeat />
            <ConditionalNavigation />

            {/* Suspense wrapper for lazy-loaded routes with loading fallback */}
            <Suspense fallback={<LoadingFallback />}>
              <Routes>
                {/* Public Routes - Lazy Loaded */}
                {RouteRenderer({ routes: publicRoutes })}

                {/* Auth Routes - Lazy Loaded */}
                {RouteRenderer({ routes: authRoutes })}

                {/* Protected Admin Routes - Lazy Loaded */}
                {RouteRenderer({ routes: protectedRoutes, protected: true })}

                {/* 404 Not Found Route - Lazy Loaded */}
                <Route
                  path={notFoundRoute.path}
                  element={
                    <>
                      <DefaultRouteSEO route={notFoundRoute} />
                      <notFoundRoute.component />
                    </>
                  }
                />
              </Routes>
            </Suspense>
            <Footer />
            <ScrollToTop />
            <CookieConsent />
            <FloatingInsuranceHelp />
            <ConditionalAnnouncement />
          </AuthProvider>
        </BrowserRouter>
        <ToastContainer />
      </div>
    </HelmetProvider>
  );
}

export default App;
